import type { Location, Route } from "vue-router"
import { computed, ref, useRoute, useRouter } from "@nuxtjs/composition-api"

enum TYPES {
  APPEND = "APPEND",
  REMOVE = "REMOVE",
}

const queue = ref<
  (
    | {
        type: TYPES.APPEND
        query: Location["query"]
      }
    | {
        type: TYPES.REMOVE
        key: string
      }
  )[]
>([])
const replacePromise = ref<Promise<Route | void>>()

export const useQueryParams = () => {
  const router = useRouter()
  const route = useRoute()

  const path = computed(() => route.value.path)
  const query = computed(() => route.value.query)

  const processQueue = () => {
    if (!queue.value.length) {
      return
    }

    if (replacePromise.value) {
      return
    }

    const queueEntry = queue.value[0]

    let query

    if (queueEntry.type === TYPES.APPEND) {
      query = { ...getLastQuery(), ...queueEntry.query }
    } else {
      query = { ...getLastQuery() }

      delete query[queueEntry.key]
    }

    replacePromise.value = router
      .replace({
        path: path.value,
        query,
      })
      .finally(() => {
        queue.value.shift()
        replacePromise.value = undefined
        processQueue()
      })
      .catch(() => {})
  }

  const getLastQuery = () => {
    return query.value
  }

  const append = (query: Record<string, string>) => {
    queue.value.push({ type: TYPES.APPEND, query })

    processQueue()
  }

  const remove = (key: string) => {
    queue.value.push({ type: TYPES.REMOVE, key })

    processQueue()
  }

  return { query, append, remove }
}
